import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaFemale } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const SSYCalculator = () => {
  const [yearlyInvestment, setYearlyInvestment] = useState(150000);
  const [girlAge, setGirlAge] = useState(5);
  const [interestRate, setInterestRate] = useState(8.2); // Current SSY rate
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [yearlyInvestment, girlAge, interestRate]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const yi = Number(yearlyInvestment) || 0;
    const ga = Number(girlAge) || 0;
    const ir = Number(interestRate) || 0;
    
    // SSY Rules:
    // - Account can be opened for girl child below 10 years
    // - Deposits allowed for 15 years from account opening
    // - Account matures after 21 years from opening OR on marriage after 18
    // - Minimum: ₹250/year, Maximum: ₹1.5 lakh/year
    
    const depositYears = 15; // Deposit period
    const maturityYears = 21; // Total maturity period
    const rate = ir / 100;
    
    let balance = 0;
    const yearlyData = [];
    
    for (let year = 1; year <= maturityYears; year++) {
      const deposit = year <= depositYears ? yi : 0;
      balance += deposit;
      const interest = balance * rate;
      balance += interest;
      
      yearlyData.push({
        year,
        age: ga + year,
        deposit,
        interest: Math.round(interest),
        balance: Math.round(balance),
      });
    }
    
    const totalDeposit = yi * depositYears;
    const totalInterest = balance - totalDeposit;
    const maturityAge = ga + maturityYears;
    
    setResult({
      maturityAmount: Math.round(balance),
      totalDeposit,
      totalInterest: Math.round(totalInterest),
      maturityAge,
      yearlyData,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Sukanya Samriddhi Yojana Report', {
      'Yearly Investment': formatCurrency(yearlyInvestment),
      'Girl\'s Current Age': `${girlAge} years`,
      'Interest Rate': `${interestRate}%`,
      'Deposit Period': '15 years',
      'Maturity Period': '21 years',
      'Total Deposit': formatCurrency(result.totalDeposit),
      'Total Interest': formatCurrency(result.totalInterest),
      'Maturity Amount': formatCurrency(result.maturityAmount),
      'Maturity Age': `${result.maturityAge} years`,
    });
  };

  const handleReset = () => {
    setYearlyInvestment(150000);
    setGirlAge(5);
    setInterestRate(8.2);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="SSY Calculator"
        description="Free Sukanya Samriddhi Yojana Calculator - Calculate SSY maturity for girl child. Current rate 8.2%."
        keywords="SSY calculator, Sukanya Samriddhi Yojana calculator, SSY maturity calculator, girl child savings scheme, SSY interest rate"
        canonicalPath="/ssy"
      />
      <div className="calculator-header">
        <h1><FaFemale style={{ color: '#ec4899' }} /> Sukanya Samriddhi Yojana (SSY)</h1>
        <p>Calculate returns for girl child savings scheme under Beti Bachao Beti Padhao</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Yearly Investment (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={yearlyInvestment === '' ? '' : yearlyInvestment}
                onChange={(e) => setYearlyInvestment(e.target.value === '' ? '' : Math.min(Number(e.target.value), 150000))}
                min="250"
                max="150000"
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Min: ₹250, Max: ₹1,50,000 per year</small>
            <div className="slider-container">
              <input
                type="range"
                min="250"
                max="150000"
                step="1000"
                value={yearlyInvestment || 0}
                onChange={(e) => setYearlyInvestment(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Girl's Current Age (Years)</label>
            <input
              type="number"
              value={girlAge === '' ? '' : girlAge}
              onChange={(e) => setGirlAge(e.target.value === '' ? '' : Math.min(Number(e.target.value), 10))}
              min="0"
              max="10"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Account can be opened for girl below 10 years</small>
          </div>

          <div className="input-group">
            <label>Interest Rate (%)</label>
            <input
              type="number"
              value={interestRate === '' ? '' : interestRate}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Current rate: 8.2% (Q1 FY 2024-25)</small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #ec4899, #be185d)' }}>
                <h3>Maturity Amount</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
                <small>When girl turns {result.maturityAge} years old</small>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Deposit (15 years)</span>
                  <span className="value">{formatCurrency(result.totalDeposit)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest Earned</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Wealth Multiplier</span>
                  <span className="value">{(result.maturityAmount / result.totalDeposit).toFixed(2)}x</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{interestRate}% p.a.</span>
                </div>
              </div>

              {/* Yearly Breakdown */}
              <div className="breakup-section">
                <h4>Year-wise Growth</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Age</th>
                        <th>Deposit</th>
                        <th>Interest</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.yearlyData.map((row, index) => (
                        <tr key={index} style={{ background: row.deposit === 0 ? '#fef3c7' : 'inherit' }}>
                          <td>{row.year}</td>
                          <td>{row.age}</td>
                          <td>{row.deposit > 0 ? formatCurrency(row.deposit) : '-'}</td>
                          <td>{formatCurrency(row.interest)}</td>
                          <td style={{ fontWeight: 'bold' }}>{formatCurrency(row.balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 SSY Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>For girl child below 10 years of age</li>
              <li>Min deposit: ₹250/year, Max: ₹1.5 lakh/year</li>
              <li>Deposit period: 15 years</li>
              <li>Maturity: 21 years from account opening</li>
              <li>Partial withdrawal (50%) allowed after age 18</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Tax benefit under Section 80C</li>
              <li>Interest is tax-free (EEE benefit)</li>
              <li>One of the highest interest rates among govt schemes</li>
              <li>Can be transferred anywhere in India</li>
              <li>Maximum 2 accounts per family</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSYCalculator;
